import type { ApplicationInsights } from "@microsoft/applicationinsights-web"

/**
 * A module-level handle to the live Application Insights instance, set by
 * {@link AppInsightsProvider} once it initializes.
 *
 * `global-error.tsx` replaces the ROOT layout when it renders, so it sits ABOVE
 * the provider tree — `useAppInsights()` context is unavailable there. This handle
 * is the escape hatch the crash screen uses to still report the exception. It also
 * serves plain (non-React) modules: the TanStack Query cache handlers and
 * `lib/analytics.ts`. When telemetry is disabled (no connection string, e.g. local
 * dev) it stays null and every helper below no-ops.
 */
let instance: ApplicationInsights | null = null

/**
 * Errors already sent to App Insights (M21). One error object can surface in
 * several places — the query cache onError, a per-call-site toast handler, and an
 * error boundary if it's rethrown into render — and each would otherwise produce
 * a duplicate exception row. WeakSet so retained error objects don't leak.
 */
const trackedErrors = new WeakSet<object>()

export function setGlobalAppInsights(ai: ApplicationInsights | null): void {
  instance = ai
}

export function isErrorTracked(error: unknown): boolean {
  return typeof error === "object" && error !== null && trackedErrors.has(error)
}

export function markErrorTracked(error: unknown): void {
  if (typeof error === "object" && error !== null) trackedErrors.add(error)
}

/** Best-effort exception report usable from outside the provider tree. No-ops when disabled. */
export function reportGlobalException(error: Error): void {
  try {
    markErrorTracked(error)
    instance?.trackException({ exception: error })
    instance?.flush()
  } catch {
    // Never let telemetry reporting throw inside an error boundary.
  }
}

/** Reports a handled error once (dedupes via the tracked set). No-ops when disabled. */
export function reportHandledError(
  error: unknown,
  properties?: Record<string, unknown>
): void {
  try {
    if (isErrorTracked(error)) return
    markErrorTracked(error)
    const exception =
      error instanceof Error ? error : new Error(String(error ?? "unknown"))
    instance?.trackException({ exception }, properties)
  } catch {
    // Telemetry must never break the app path that observed the error.
  }
}

/** Custom event for the analytics seam (lib/analytics.ts). No-ops when disabled. */
export function trackGlobalEvent(
  name: string,
  properties?: Record<string, unknown>
): void {
  try {
    instance?.trackEvent({ name }, properties)
  } catch {
    // Analytics is fire-and-forget.
  }
}

/** Numeric metric (web-vitals). No-ops when disabled. */
export function trackGlobalMetric(name: string, value: number): void {
  try {
    instance?.trackMetric({ name, average: value })
  } catch {
    // Telemetry is fire-and-forget.
  }
}

/**
 * Tags all subsequent telemetry with the signed-in user's pseudonymous Entra OID
 * (ai.user.authenticatedId). OID ONLY — never email or name (M21 privacy stance;
 * the OID is a GUID that identifies the account without exposing PII).
 */
export function setAuthenticatedUser(oid: string): void {
  try {
    instance?.setAuthenticatedUserContext(oid, undefined, true)
  } catch {
    // Telemetry is fire-and-forget.
  }
}

export function clearAuthenticatedUser(): void {
  try {
    instance?.clearAuthenticatedUserContext()
  } catch {
    // Telemetry is fire-and-forget.
  }
}
